// Round-trip-safe output builder. Untouched frames + untouched keypoints come
// out byte-identical to the input; only kpts the user actually changed get a
// non-null `status` (and, for manual_annotated, updated `pixel_coords`).
// `confidence_score` is never modified.
//
// (Platform port: the file-writing paths — File System Access / download / the
// loader.save() entrypoint — were dropped. Serialization to .toon + the
// /pose/commit write live in ../../io/serialize.ts. This file is now the pure
// edit-merge builder only.)

import { KEYPOINT_NAMES } from '../../config/keypoints';
import type { EditMap, KeypointName, NewKeypointRaw, NewPredictionsFile } from '../../types';

function cloneKpt(k: NewKeypointRaw): NewKeypointRaw {
  // Canonical toon output keeps out_of_frame coordinates explicit.
  const pc =
    k.status === 'out_of_frame'
      ? ([-1, -1] as [number, number])
      : k.pixel_coords == null
        ? null
        : ([k.pixel_coords[0], k.pixel_coords[1]] as [number, number]);
  return {
    pixel_coords: pc,
    confidence_score: k.confidence_score,
    status: k.status,
  };
}

function findFrameKey(frames: Record<string, unknown>, frameIdx: number): string | null {
  // Fast path: try the canonical 6-digit zero-padded key first.
  const padded = frameIdx.toString().padStart(6, '0');
  if (padded in frames) return padded;
  // Fallback: linear scan for any numeric-equivalent key.
  for (const k of Object.keys(frames)) {
    if (Number(k) === frameIdx) return k;
  }
  return null;
}

/**
 * Build a new-format predictions file with user statuses merged in.
 * Deep-clones the input so mutation here never leaks back into the store.
 * `visibleNames` restricts which keypoints are considered for edits (so side
 * cameras don't gain ghost edits for joints they don't render).
 */
export function buildNewFormatOutput(
  raw: NewPredictionsFile,
  edits: EditMap,
  visibleNames: ReadonlySet<KeypointName> = KEYPOINT_NAMES
): NewPredictionsFile {
  const out: NewPredictionsFile = {
    metadata: {
      ...raw.metadata,
      image_size: [...raw.metadata.image_size] as [number, number],
      support_keypoints: [...raw.metadata.support_keypoints],
    },
    frames: {},
  };

  // Deep-clone every frame so unedited frames are byte-identical to input.
  for (const [frameKey, frameRaw] of Object.entries(raw.frames)) {
    if (!frameRaw || typeof frameRaw !== 'object') continue;
    const cloned: Record<string, NewKeypointRaw> = {};
    for (const [kptName, kptRaw] of Object.entries(frameRaw)) {
      cloned[kptName] = cloneKpt(kptRaw);
    }
    out.frames[frameKey] = cloned;
  }

  // Merge edits.
  for (const [frameKeyStr, frameEdits] of Object.entries(edits)) {
    if (!frameEdits) continue;
    const frameIdx = Number(frameKeyStr);
    if (!Number.isFinite(frameIdx) || !Number.isInteger(frameIdx) || frameIdx < 0) continue;

    const stringKey = findFrameKey(out.frames, frameIdx);
    if (stringKey === null) continue;
    const frameOut = out.frames[stringKey];
    if (!frameOut) continue;

    for (const [kptNameStr, edit] of Object.entries(frameEdits)) {
      if (!edit) continue;
      if (edit.seeded) continue;
      const kptName = kptNameStr as KeypointName;
      if (!visibleNames.has(kptName)) continue;
      // The keypoint usually exists in the input, possibly with null coords.
      // If it is entirely absent yet the user annotated it, invent a minimal
      // entry so the edit is not silently dropped on save. confidence_score has
      // no source value here, so it starts at 0.
      let original = frameOut[kptName];
      if (!original) {
        original = { pixel_coords: null, confidence_score: 0, status: null };
        frameOut[kptName] = original;
      }

      if (edit.visibility === 'out_of_frame') {
        original.pixel_coords = [-1, -1];
        original.status = 'out_of_frame';
      } else if (edit.visibility === 'occluded') {
        // Persist the annotator's placed position. This matters for secPose v2,
        // where the synthesized base prediction has null coords.
        if (edit.xy[0] !== -1 || edit.xy[1] !== -1) {
          original.pixel_coords = [edit.xy[0], edit.xy[1]];
        }
        original.status = 'occluded';
      } else if (edit.visibility === 'visible') {
        const movedX = original.pixel_coords == null || edit.xy[0] !== original.pixel_coords[0];
        const movedY = original.pixel_coords == null || edit.xy[1] !== original.pixel_coords[1];
        if (movedX || movedY) {
          original.pixel_coords = [edit.xy[0], edit.xy[1]];
          original.status = 'manual_annotated';
        }
        // Unmoved + visibility=visible: user merely unlocked a qualified
        // kpt without correcting. Per spec, leave status as-is (null).
      }
    }
  }

  return out;
}
